/* VentureWrist — Watch3D: a physically-based product renderer for the design studio.
 *
 * This replaces the prototype's original renderer (smartwatch-sim.html lines
 * 3514-3856) with a modern PBR pipeline. The *catalog semantics* are unchanged —
 * every COMPONENTS id still maps to the same material story, and the per-brand dial
 * identity (faceTheme/drawFace) is preserved exactly — but the image quality is a
 * generation ahead:
 *
 *   · ACES filmic tone mapping and correct colour management
 *   · a procedural studio IBL (softboxes through PMREMGenerator) so metals actually
 *     reflect something instead of reading as flat grey
 *   · MeshPhysicalMaterial throughout: clearcoat, sheen, anisotropy, iridescent
 *     AR-coated crystal, and procedurally generated normal/roughness maps
 *   · lathe-built cases with real chamfers, a dial seat and a domed caseback
 *   · straps swept along a wrist curve with a tapered belt cross-section, plus
 *     stitching, adjustment holes and a real pin buckle
 *   · a soft-shadow stage, contact pool and backlight glow
 *   · damped orbit with drag inertia, pointer parallax, an entrance animation,
 *     ResizeObserver sizing, and RAF gating when offscreen or backgrounded
 *
 * The public API (buildWatch / mountLive / updateLive / unmountLive / snapshot /
 * prodCfg / prodKey / genKey / drawFace / faceTheme / hasGL) is unchanged, so
 * WatchViewer.tsx and the product-thumbnail callers keep working untouched.
 *
 * A mirror of this file ships with the marketing site (website2/assets/js/watch3d.js);
 * regenerate it with `node website2/sync-watch3d.mjs` after editing this one. */
/* eslint-disable */
// @ts-nocheck
import * as THREE from 'https://esm.sh/three@0.169.0';
import { COMPONENTS } from '@venturewrist/engine';
import { cssColorHex } from '../lib/cssColor';

/* ═══════════════════ 1 · tunables ═══════════════════ */

const TUNE = {
  exposure: 1.18,
  dpr: 2,            // pixel-ratio ceiling
  shadowMap: 2048,
  fov: 30,           // long lens: product-photo compression, less wide-angle distortion
  camera: [0, 3.9, 7.1],
  target: [0, -0.30, 0],
  tilt: { x: 0.52, z: -0.11 },   // resting pose: tipped toward the viewer, like a display stand
  sway: 0.42,                    // idle presentation sweep, radians either side of base
  swayRate: 0.011,                // ~9 s per cycle at 60fps
};

/* ═══════════════════ 2 · procedural micro-surface textures ═══════════════════
 * Everything the materials need is generated on a canvas at first use and cached.
 * No binary assets, so this drops into the app and the static marketing site alike. */

function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function blankCanvas(size) {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  return c;
}

/** Tileable value-noise height field, fBm over `octaves` doublings of `base`. */
function noiseCanvas(size, octaves, base, seed) {
  const c = blankCanvas(size), x = c.getContext('2d');
  const img = x.createImageData(size, size);
  const rnd = mulberry32(seed);
  const grids = [];
  for (let o = 0; o < octaves; o++) {
    const n = base << o, g = new Float32Array(n * n);
    for (let i = 0; i < g.length; i++) g[i] = rnd();
    grids.push({ n, g });
  }
  const sample = (gd, u, v) => {
    const n = gd.n, fx = u * n, fy = v * n;
    const x0 = ((fx | 0) % n + n) % n, y0 = ((fy | 0) % n + n) % n;
    const x1 = (x0 + 1) % n, y1 = (y0 + 1) % n;
    const tx = fx - Math.floor(fx), ty = fy - Math.floor(fy);
    const sx = tx * tx * (3 - 2 * tx), sy = ty * ty * (3 - 2 * ty);
    const a = gd.g[y0 * n + x0], b = gd.g[y0 * n + x1], cc = gd.g[y1 * n + x0], d = gd.g[y1 * n + x1];
    return (a * (1 - sx) + b * sx) * (1 - sy) + (cc * (1 - sx) + d * sx) * sy;
  };
  for (let y = 0; y < size; y++) for (let xx = 0; xx < size; xx++) {
    let v = 0, amp = 1, sum = 0;
    for (let o = 0; o < octaves; o++) { v += sample(grids[o], xx / size, y / size) * amp; sum += amp; amp *= 0.5; }
    v /= sum;
    const i = (y * size + xx) * 4, g = (v * 255) | 0;
    img.data[i] = img.data[i + 1] = img.data[i + 2] = g; img.data[i + 3] = 255;
  }
  x.putImageData(img, 0, 0);
  return c;
}

/** Sobel a grayscale height canvas into a tangent-space normal map. */
function heightToNormal(src, strength) {
  const w = src.width, h = src.height;
  const sd = src.getContext('2d').getImageData(0, 0, w, h).data;
  const out = blankCanvas(w), ox = out.getContext('2d');
  const img = ox.createImageData(w, h);
  const at = (x, y) => sd[((((y % h) + h) % h) * w + (((x % w) + w) % w)) * 4] / 255;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const dx = (at(x + 1, y) - at(x - 1, y)) * strength;
    const dy = (at(x, y + 1) - at(x, y - 1)) * strength;
    let nx = -dx, ny = -dy, nz = 1;
    const l = Math.hypot(nx, ny, nz) || 1; nx /= l; ny /= l; nz /= l;
    const i = (y * w + x) * 4;
    img.data[i] = (nx * 0.5 + 0.5) * 255;
    img.data[i + 1] = (ny * 0.5 + 0.5) * 255;
    img.data[i + 2] = (nz * 0.5 + 0.5) * 255;
    img.data[i + 3] = 255;
  }
  ox.putImageData(img, 0, 0);
  return out;
}

const TEX = {
  _c: {},
  _mk(key, build, repeat) {
    if (!this._c[key]) {
      const t = new THREE.CanvasTexture(build());
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      t.anisotropy = 8;
      this._c[key] = t;
    }
    // clone so each surface picks its own tiling without fighting the others
    const t = this._c[key].clone();
    t.needsUpdate = true;
    if (repeat) t.repeat.set(repeat[0], repeat[1]);
    return t;
  },

  /** Fine unidirectional streaks: brushed aluminium / titanium roughness. */
  brushedRough(repeat) {
    return this._mk('brushR', () => {
      const S = 512, c = blankCanvas(S), x = c.getContext('2d');
      x.fillStyle = '#8a8a8a'; x.fillRect(0, 0, S, S);
      const rnd = mulberry32(7);
      for (let i = 0; i < 5200; i++) {
        const y = rnd() * S, len = 40 + rnd() * 260, x0 = rnd() * S;
        const g = (Math.max(0, Math.min(1, 0.5 + (rnd() - 0.5) * 0.62)) * 255) | 0;
        x.strokeStyle = 'rgba(' + g + ',' + g + ',' + g + ',0.30)';
        x.lineWidth = rnd() < 0.75 ? 1 : 2;
        x.beginPath(); x.moveTo(x0, y); x.lineTo(x0 + len, y + (rnd() - 0.5) * 1.2); x.stroke();
      }
      return c;
    }, repeat);
  },
  brushedNormal(repeat) {
    return this._mk('brushN', () => {
      const S = 512, c = blankCanvas(S), x = c.getContext('2d');
      x.fillStyle = '#808080'; x.fillRect(0, 0, S, S);
      const rnd = mulberry32(11);
      for (let i = 0; i < 3400; i++) {
        const y = rnd() * S, g = ((rnd() < 0.5 ? 0.36 : 0.66) * 255) | 0;
        x.strokeStyle = 'rgba(' + g + ',' + g + ',' + g + ',0.5)';
        x.lineWidth = 1;
        x.beginPath(); x.moveTo(rnd() * S, y); x.lineTo(rnd() * S + 200, y); x.stroke();
      }
      return heightToNormal(c, 2.2);
    }, repeat);
  },

  /** Silicone: dense fine speckle, very low relief. */
  siliconeNormal(repeat) {
    return this._mk('silN', () => heightToNormal(noiseCanvas(256, 4, 32, 3), 1.1), repeat);
  },

  /** Leather: coarse grain plus a few pressed creases. */
  leatherNormal(repeat) {
    return this._mk('leaN', () => {
      const base = noiseCanvas(512, 5, 12, 21);
      const x = base.getContext('2d');
      x.globalCompositeOperation = 'multiply';
      const rnd = mulberry32(5);
      for (let i = 0; i < 26; i++) {
        x.strokeStyle = 'rgba(40,40,40,' + (0.10 + rnd() * 0.16).toFixed(3) + ')';
        x.lineWidth = 1 + rnd() * 2.4;
        x.beginPath();
        let px = rnd() * 512, py = rnd() * 512;
        x.moveTo(px, py);
        for (let s = 0; s < 5; s++) {
          px += (rnd() - 0.5) * 190; py += (rnd() - 0.5) * 190;
          x.lineTo(px, py);
        }
        x.stroke();
      }
      x.globalCompositeOperation = 'source-over';
      return heightToNormal(base, 3.0);
    }, repeat);
  },

  /** Woven nylon: an over-under twill. */
  weaveNormal(repeat) {
    return this._mk('wvN', () => {
      const S = 256, cell = 16, c = blankCanvas(S), x = c.getContext('2d');
      x.fillStyle = '#606060'; x.fillRect(0, 0, S, S);
      for (let gy = 0; gy < S / cell; gy++) for (let gx = 0; gx < S / cell; gx++) {
        const over = (gx + gy) % 2 === 0;
        const px = gx * cell, py = gy * cell;
        const g = x.createLinearGradient(px, py, over ? px + cell : px, over ? py : py + cell);
        g.addColorStop(0, '#4a4a4a'); g.addColorStop(0.5, '#d2d2d2'); g.addColorStop(1, '#4a4a4a');
        x.fillStyle = g;
        x.fillRect(px + 0.5, py + 0.5, cell - 1, cell - 1);
      }
      return heightToNormal(c, 2.4);
    }, repeat);
  },

  /** Injection-moulded polycarbonate: near-flat with a faint pebble. */
  polyNormal(repeat) {
    return this._mk('polyN', () => heightToNormal(noiseCanvas(256, 3, 48, 9), 0.7), repeat);
  },

  /** Soft radial falloff, used for the contact pool and the backlight glow. */
  radial(inner, gamma) {
    const key = 'rad' + inner + '_' + gamma;
    if (!this._c[key]) {
      const S = 256, c = blankCanvas(S), x = c.getContext('2d');
      const img = x.createImageData(S, S);
      for (let y = 0; y < S; y++) for (let px = 0; px < S; px++) {
        const dx = (px - S / 2) / (S / 2), dy = (y - S / 2) / (S / 2);
        const d = Math.min(1, Math.hypot(dx, dy));
        const a = Math.pow(Math.max(0, 1 - Math.max(0, (d - inner) / (1 - inner))), gamma);
        const i = (y * S + px) * 4;
        img.data[i] = img.data[i + 1] = img.data[i + 2] = 255;
        img.data[i + 3] = (a * 255) | 0;
      }
      x.putImageData(img, 0, 0);
      const t = new THREE.CanvasTexture(c);
      t.colorSpace = THREE.SRGBColorSpace;
      this._c[key] = t;
    }
    return this._c[key];
  },
};

/* ═══════════════════ 3 · studio IBL ═══════════════════
 * A three-point lighting rig built as emissive geometry and prefiltered into a
 * mip-chained environment map. This is the single largest quality jump: PBR metal
 * with nothing to reflect always reads dull, no matter how the lights are set. */

/* Cached per renderer *and* per theme: the room tint changes with the theme, so
 * a switch has to rebuild the prefiltered map rather than reuse a stale one. */
const ENV_CACHE = new WeakMap();

function studioEnv(renderer) {
  const theme = (typeof document !== 'undefined'
    && document.documentElement.getAttribute('data-theme')) || 'dark';
  const cached = ENV_CACHE.get(renderer);
  if (cached && cached.theme === theme) return cached.texture;
  if (cached) cached.texture.dispose();

  const scene = new THREE.Scene();
  const box = new THREE.BoxGeometry();
  box.deleteAttribute('uv');

  // The surrounding room. Dark themes get a cool slate box; light themes a warm
  // bright one, so the piece reflects the surface it is presented on. The rest
  // of the rig (softbox positions, gains, tints) is photographic and fixed.
  const shell = new THREE.Mesh(box, new THREE.MeshStandardMaterial({
    side: THREE.BackSide,
    color: cssColorHex(renderer.domElement, 'stage-room', 0x232c3c),
    roughness: 1, metalness: 0,
  }));
  shell.scale.set(34, 20, 34);
  scene.add(shell);

  const fill = new THREE.PointLight(0xffffff, 42, 46, 2);
  fill.position.set(0, 5, 0);
  scene.add(fill);

  const softbox = (hex, gain, pos, scale, rot) => {
    const m = new THREE.Mesh(box, new THREE.MeshBasicMaterial({
      color: new THREE.Color(hex).multiplyScalar(gain),
    }));
    m.position.set(pos[0], pos[1], pos[2]);
    m.scale.set(scale[0], scale[1], scale[2]);
    if (rot) m.rotation.set(rot[0], rot[1], rot[2]);
    scene.add(m);
  };

  softbox(0xffffff, 9.0, [-2.2, 8.6, 3.2], [6.5, 0.3, 4.5], [0.18, 0, 0.22]); // key, high and off-axis
  softbox(0xcadcff, 4.2, [-9.5, 2.2, 4.5], [0.3, 7, 9]);                 // cool fill, camera-left
  softbox(0xffd7a4, 3.4, [9.5, 3.4, -4.0], [0.3, 6, 8]);                 // warm rim, camera-right/back
  softbox(0xffffff, 0.9, [0, -6.5, 3.5], [11, 0.3, 6]);                  // bounce off the "table"
  softbox(0xa9c6ff, 2.6, [0, 2.5, -10.5], [12, 7, 0.3]);                 // back wall: crisp edge highlight

  const pmrem = new THREE.PMREMGenerator(renderer);
  const rt = pmrem.fromScene(scene, 0.02, 0.1, 90);
  pmrem.dispose();
  box.dispose();
  shell.material.dispose();

  ENV_CACHE.set(renderer, { theme, texture: rt.texture });
  return rt.texture;
}

/* ═══════════════════ 4 · material library ═══════════════════ */

const GOLD = 0xd4af6a;

function caseMaterial(k, colorHex) {
  const color = colorHex != null ? colorHex : k.color;
  switch (k.id) {
    case 'k1': // polycarbonate: moulded, satin clearcoat
      return new THREE.MeshPhysicalMaterial({
        color, metalness: 0, roughness: 0.50,
        clearcoat: 0.72, clearcoatRoughness: 0.30,
        normalMap: TEX.polyNormal([3, 3]), normalScale: new THREE.Vector2(0.20, 0.20),
        envMapIntensity: 0.95,
      });
    case 'k2': // aluminium: fine brush
      return new THREE.MeshPhysicalMaterial({
        color, metalness: 1, roughness: 0.29, anisotropy: 0.85,
        roughnessMap: TEX.brushedRough([2, 2]),
        normalMap: TEX.brushedNormal([2, 2]), normalScale: new THREE.Vector2(0.11, 0.11),
        envMapIntensity: 1.2,
      });
    case 'k3': // stainless: mirror polished
      return new THREE.MeshPhysicalMaterial({
        color, metalness: 1, roughness: 0.10, envMapIntensity: 1.55,
      });
    case 'k4': // titanium: matte, slightly warm, directional
      return new THREE.MeshPhysicalMaterial({
        color, metalness: 0.97, roughness: 0.37, anisotropy: 0.55,
        roughnessMap: TEX.brushedRough([2, 2]),
        normalMap: TEX.brushedNormal([2, 2]), normalScale: new THREE.Vector2(0.09, 0.09),
        envMapIntensity: 1.2,
      });
    default: // k5 ceramic: glazed, deep clearcoat
      return new THREE.MeshPhysicalMaterial({
        color, metalness: 0, roughness: 0.15,
        clearcoat: 1, clearcoatRoughness: 0.045,
        sheen: 0.28, sheenColor: new THREE.Color(0xffffff), sheenRoughness: 0.4,
        envMapIntensity: 1.4,
      });
  }
}

function strapMaterial(s, cfg) {
  const color = s.color != null ? s.color : (cfg.colorHex != null ? cfg.colorHex : 0x23272f);
  switch (s.id) {
    case 's2': // woven fabric
      return new THREE.MeshPhysicalMaterial({
        color, metalness: 0, roughness: 0.95,
        sheen: 1, sheenRoughness: 0.62,
        sheenColor: new THREE.Color(color).lerp(new THREE.Color(0xffffff), 0.55),
        normalMap: TEX.weaveNormal([3, 26]), normalScale: new THREE.Vector2(0.80, 0.80),
        envMapIntensity: 0.7,
      });
    case 's3': // leather
      return new THREE.MeshPhysicalMaterial({
        color, metalness: 0, roughness: 0.56,
        clearcoat: 0.24, clearcoatRoughness: 0.58,
        normalMap: TEX.leatherNormal([2, 14]), normalScale: new THREE.Vector2(0.72, 0.72),
        envMapIntensity: 0.85,
      });
    case 's4': // metal bracelet
      return new THREE.MeshPhysicalMaterial({
        color, metalness: 1, roughness: 0.19, anisotropy: 0.6,
        roughnessMap: TEX.brushedRough([1, 1]),
        envMapIntensity: 1.45,
      });
    default: // s1 silicone sport
      return new THREE.MeshPhysicalMaterial({
        color, metalness: 0, roughness: 0.60,
        clearcoat: 0.38, clearcoatRoughness: 0.48,
        normalMap: TEX.siliconeNormal([4, 30]), normalScale: new THREE.Vector2(0.52, 0.52),
        envMapIntensity: 0.9,
      });
  }
}

/** Cover crystal. Sapphire-class panels get a tighter polish and a stronger
 *  anti-reflective iridescence: the faint blue-violet bloom of a real one. */
function glassMaterial(displayId) {
  const sapphire = displayId === 'd3' || displayId === 'd4' || displayId === 'd5';
  return new THREE.MeshPhysicalMaterial({
    color: 0xffffff,
    metalness: 0, roughness: sapphire ? 0.012 : 0.045,
    transparent: true, opacity: sapphire ? 0.07 : 0.10,
    clearcoat: 1, clearcoatRoughness: 0.012,
    iridescence: sapphire ? 0.40 : 0.14,
    iridescenceIOR: 1.32,
    iridescenceThicknessRange: [120, 420],
    envMapIntensity: 1.5,
    depthWrite: false,
    side: THREE.FrontSide,
  });
}

function polishedMetal(color, roughness) {
  return new THREE.MeshPhysicalMaterial({
    color, metalness: 1, roughness: roughness == null ? 0.22 : roughness, envMapIntensity: 1.4,
  });
}

/* ═══════════════════ 5 · geometry helpers ═══════════════════ */

/** Append an arc of Vector2s to a lathe profile. */
function arcInto(pts, cx, cy, r, a0, a1, n) {
  for (let i = 1; i <= n; i++) {
    const a = a0 + (a1 - a0) * (i / n);
    pts.push(new THREE.Vector2(cx + Math.cos(a) * r, cy + Math.sin(a) * r));
  }
  return pts;
}

/** Superellipse. n≈2 is an ellipse, n≈4.6 is the squircle a modern watch case uses. */
function squircleShape(a, n, seg) {
  const sh = new THREE.Shape();
  for (let i = 0; i <= seg; i++) {
    const t = (i / seg) * Math.PI * 2;
    const c = Math.cos(t), s = Math.sin(t);
    const x = Math.sign(c) * Math.pow(Math.abs(c), 2 / n) * a;
    const y = Math.sign(s) * Math.pow(Math.abs(s), 2 / n) * a;
    if (i === 0) sh.moveTo(x, y); else sh.lineTo(x, y);
  }
  sh.closePath();
  return sh;
}

/** Rounded rectangle as a Shape (filled) or a Path (usable as a hole). */
function roundedRect(w, h, r, asPath) {
  const p = asPath ? new THREE.Path() : new THREE.Shape();
  const x = w / 2 - r, y = h / 2 - r;
  p.moveTo(-x, -h / 2);
  p.lineTo(x, -h / 2);
  p.quadraticCurveTo(w / 2, -h / 2, w / 2, -y);
  p.lineTo(w / 2, y);
  p.quadraticCurveTo(w / 2, h / 2, x, h / 2);
  p.lineTo(-x, h / 2);
  p.quadraticCurveTo(-w / 2, h / 2, -w / 2, y);
  p.lineTo(-w / 2, -y);
  p.quadraticCurveTo(-w / 2, -h / 2, -x, -h / 2);
  return p;
}

/** A box with rounded edges on every axis. */
function roundedBox(w, h, d, r) {
  const bev = Math.min(r, d * 0.48);
  const g = new THREE.ExtrudeGeometry(roundedRect(w, h, r), {
    depth: d - 2 * bev, bevelEnabled: true,
    bevelThickness: bev, bevelSize: bev, bevelSegments: 3, curveSegments: 8,
  });
  g.center();
  return g;
}

/**
 * A right-handed basis from two perpendicular axes. Composing a quaternion from a
 * left-handed basis silently yields a reflection, so the third axis is always
 * derived rather than supplied.
 */
function basisQuat(xAxis, yAxis) {
  const z = new THREE.Vector3().crossVectors(xAxis, yAxis).normalize();
  return new THREE.Quaternion().setFromRotationMatrix(
    new THREE.Matrix4().makeBasis(xAxis, yAxis, z));
}

/**
 * Belt cross-section: a superellipse on unit half-extents, carrying outward 2D
 * normals. The sweeper scales it per step, so flatness comes from width ≫ thickness
 * rather than from the section itself.
 */
function beltSection(n, samples) {
  const pts = [];
  for (let i = 0; i < samples; i++) {
    const t = (i / samples) * Math.PI * 2;
    const c = Math.cos(t), s = Math.sin(t);
    pts.push({
      x: Math.sign(c) * Math.pow(Math.abs(c), 2 / n),
      y: Math.sign(s) * Math.pow(Math.abs(s), 2 / n),
    });
  }
  for (let i = 0; i < samples; i++) {
    const p0 = pts[(i - 1 + samples) % samples], p1 = pts[(i + 1) % samples];
    let tx = p1.x - p0.x, ty = p1.y - p0.y;
    const l = Math.hypot(tx, ty) || 1;
    pts[i].nx = ty / l; pts[i].ny = -tx / l;
  }
  return pts;
}

/**
 * Sweep a tapering cross-section along a curve.
 *
 * Every strap curve here is planar (it lives in the y-z plane), and Frenet frames
 * are ill-conditioned on planar curves — three picks the initial normal from the
 * smallest tangent component, which for an x-free curve lands on X and twists the
 * belt onto its side. So the width axis is pinned to world X and the thickness axis
 * is tangent × X: exact, stable, and the strap can never corkscrew.
 *
 * Returns the geometry plus the per-step frames, so callers can hang stitching,
 * buckles and adjustment holes off the same centreline.
 */
function sweep(curve, steps, section, widthAt, thickAt, uvRepeat) {
  const N = section.length;
  const pos = [], nor = [], uvs = [], idx = [];
  const P = new THREE.Vector3(), T = new THREE.Vector3();
  const W = new THREE.Vector3(1, 0, 0), NM = new THREE.Vector3();
  const frames = [];

  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    curve.getPointAt(t, P);
    curve.getTangentAt(t, T);
    NM.crossVectors(T, W).normalize();
    const w = widthAt(t), th = thickAt(t);
    frames.push({ p: P.clone(), t: T.clone(), n: NM.clone(), w, th });
    for (let j = 0; j < N; j++) {
      const s = section[j];
      pos.push(
        P.x + W.x * s.x * w + NM.x * s.y * th,
        P.y + W.y * s.x * w + NM.y * s.y * th,
        P.z + W.z * s.x * w + NM.z * s.y * th,
      );
      // a non-uniform scale transforms normals by its inverse: divide, don't multiply
      const ax = s.nx / w, ay = s.ny / th;
      const nx = W.x * ax + NM.x * ay, ny = W.y * ax + NM.y * ay, nz = W.z * ax + NM.z * ay;
      const l = Math.hypot(nx, ny, nz) || 1;
      nor.push(nx / l, ny / l, nz / l);
      uvs.push(j / N, t * uvRepeat);
    }
  }
  // Winding: cross(b-a, c-a) = N×T = +W, i.e. outward. Reversing these two triples
  // turns the strap inside out.
  for (let i = 0; i < steps; i++) for (let j = 0; j < N; j++) {
    const j2 = (j + 1) % N;
    const a = i * N + j, b = i * N + j2, c = (i + 1) * N + j, d = (i + 1) * N + j2;
    idx.push(a, b, c, b, d, c);
  }
  const cap = (fi, flip) => {
    const f = frames[fi], base = pos.length / 3;
    pos.push(f.p.x, f.p.y, f.p.z);
    nor.push(f.t.x * flip, f.t.y * flip, f.t.z * flip);
    uvs.push(0.5, 0.5);
    const ring = fi * N;
    for (let j = 0; j < N; j++) {
      const j2 = (j + 1) % N;
      if (flip > 0) idx.push(base, ring + j, ring + j2);
      else idx.push(base, ring + j2, ring + j);
    }
  };
  cap(0, -1);
  cap(steps, 1);

  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  g.setIndex(idx);
  return { geometry: g, frames };
}

/**
 * Lathe profile for a round case: dial seat, bezel top face, chamfered top edge,
 * side wall, chamfered bottom edge, caseback flat, domed caseback. y = 0 is the
 * bezel top and the case hangs below it.
 */
function roundCaseProfile(R, H, dialR, seat, cTop, cBot, backR, backDome) {
  const p = [];
  p.push(new THREE.Vector2(0, -seat));            // seat floor, centre
  p.push(new THREE.Vector2(dialR, -seat));        // seat floor, edge
  p.push(new THREE.Vector2(dialR, 0));            // seat wall up to the bezel
  p.push(new THREE.Vector2(R - cTop, 0));         // bezel top face
  arcInto(p, R - cTop, -cTop, cTop, Math.PI / 2, 0, 7);        // rounded top edge
  p.push(new THREE.Vector2(R, -H + cBot));        // side wall
  arcInto(p, R - cBot, -H + cBot, cBot, 0, -Math.PI / 2, 7);   // rounded bottom edge
  p.push(new THREE.Vector2(backR, -H));           // caseback flat
  for (let i = 1; i <= 12; i++) {                 // caseback dome
    const a = (i / 12) * (Math.PI / 2);
    p.push(new THREE.Vector2(backR * Math.cos(a), -H - backDome * Math.sin(a)));
  }
  return p;
}

/* ═══════════════════ 6 · the watch ═══════════════════ */

export const Watch3D = {
  live: null,

  hasGL() {
    if (typeof THREE === 'undefined') return false;
    try {
      const c = document.createElement('canvas');
      return !!(c.getContext('webgl2') || c.getContext('webgl'));
    } catch (e) { return false; }
  },

  buildWatch(cfg) {
    const grp = new THREE.Group();
    const k = COMPONENTS.casing.find((x) => x.id === cfg.casing) || COMPONENTS.casing[1];
    const s = COMPONENTS.strap.find((x) => x.id === cfg.strap) || COMPONENTS.strap[0];
    const sz = COMPONENTS.sizes.find((x) => x.id === cfg.size) || COMPONENTS.sizes[1];
    const st = COMPONENTS.style.find((x) => x.id === cfg.style) || COMPONENTS.style[2];
    const d = COMPONENTS.display.find((x) => x.id === cfg.display) || COMPONENTS.display[1];

    const scale = sz.scale;
    const square = st.shape === 'square';
    const caseColor = cfg.colorHex != null ? cfg.colorHex : k.color;
    const caseMat = caseMaterial(k, cfg.colorHex);
    const theme = this.faceTheme(cfg);
    const accNum = theme.accent;
    const X = new THREE.Vector3(1, 0, 0);
    const UP = new THREE.Vector3(0, 1, 0);

    // meshes join the group already casting and receiving — self-shadowing between
    // case, lugs and strap is most of what sells the form
    const add = (mesh) => { mesh.castShadow = true; mesh.receiveShadow = true; grp.add(mesh); return mesh; };

    let caseR, dialR, screenY, glassY, crownY, caseBottomY, caseH;

    if (square) {
      /* Modern — squircle, edge-to-edge glass sitting proud of the case. */
      const w = 1.06 * scale;
      caseH = 0.40 * scale;
      const bev = 0.06 * scale;
      const geo = new THREE.ExtrudeGeometry(squircleShape(w, 4.6, 168), {
        depth: caseH - 2 * bev, bevelEnabled: true,
        bevelThickness: bev, bevelSize: bev, bevelSegments: 6, curveSegments: 10,
      });
      geo.rotateX(-Math.PI / 2);                     // extrude axis +Z becomes +Y
      geo.computeBoundingBox();
      const top = geo.boundingBox.max.y, bot = geo.boundingBox.min.y;
      geo.translate(0, -top, 0);                     // top face lands on y = 0
      add(new THREE.Mesh(geo, caseMat));

      caseR = w;
      dialR = w * 0.895;
      screenY = 0.005 * scale;
      glassY = 0.020 * scale;
      crownY = -caseH * 0.42;
      caseBottomY = bot - top;
    } else {
      /* Round — sporty runs taller with a chunky bezel, classic is the slimmest. */
      caseH = (st.id === 'sporty' ? 0.46 : st.id === 'classic' ? 0.33 : 0.38) * scale;
      caseR = (st.id === 'classic' ? 1.06 : 1.10) * scale;
      dialR = caseR * (st.id === 'sporty' ? 0.845 : st.id === 'luxury' ? 0.83 : 0.87);
      const seat = 0.055 * scale;
      const cTop = (st.id === 'classic' ? 0.05 : 0.075) * scale;
      const cBot = 0.09 * scale;
      const prof = roundCaseProfile(caseR, caseH, dialR, seat, cTop, cBot, caseR * 0.60, 0.14 * scale);
      add(new THREE.Mesh(new THREE.LatheGeometry(prof, 128), caseMat));

      screenY = -seat + 0.005 * scale;
      glassY = -0.012 * scale;
      crownY = -caseH * 0.44;
      caseBottomY = -caseH - 0.14 * scale;

      /* Bezel treatment — the strongest visual signature of each design language. */
      if (st.id === 'luxury') {
        const ring = new THREE.Mesh(
          new THREE.TorusGeometry(caseR * 0.985, 0.062 * scale, 24, 128), polishedMetal(GOLD, 0.14));
        ring.rotation.x = Math.PI / 2;
        ring.position.y = -0.014 * scale;
        add(ring);
        // fluting: fine radial ribs cut into the bezel edge
        const N = 56;
        const flute = new THREE.InstancedMesh(
          roundedBox(0.05 * scale, 0.075 * scale, 0.15 * scale, 0.016 * scale),
          polishedMetal(GOLD, 0.30), N);
        const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), one = new THREE.Vector3(1, 1, 1);
        for (let i = 0; i < N; i++) {
          const a = (i / N) * Math.PI * 2;
          q.setFromAxisAngle(UP, -a);
          m4.compose(new THREE.Vector3(
            Math.cos(a) * caseR * 0.99, -0.036 * scale, Math.sin(a) * caseR * 0.99), q, one);
          flute.setMatrixAt(i, m4);
        }
        flute.castShadow = true;
        grp.add(flute);
      } else if (st.id === 'sporty') {
        const ring = new THREE.Mesh(
          new THREE.TorusGeometry(caseR * 0.90, 0.042 * scale, 20, 96),
          new THREE.MeshPhysicalMaterial({
            color: caseColor, metalness: Math.max(0.4, k.metal), roughness: 0.44,
            clearcoat: 0.35, clearcoatRoughness: 0.45, envMapIntensity: 1.0,
          }));
        ring.rotation.x = Math.PI / 2;
        ring.position.y = -0.02 * scale;
        add(ring);
        const accMat = new THREE.MeshPhysicalMaterial({
          color: accNum, metalness: 0.3, roughness: 0.35,
          emissive: accNum, emissiveIntensity: 0.55, envMapIntensity: 1.1,
        });
        [0, Math.PI / 2, Math.PI, Math.PI * 1.5].forEach((a) => {
          const b = new THREE.Mesh(
            roundedBox(0.072 * scale, 0.052 * scale, 0.15 * scale, 0.018 * scale), accMat);
          b.position.set(Math.cos(a) * caseR * 0.90, 0.028 * scale, Math.sin(a) * caseR * 0.90);
          b.rotation.y = -a;
          add(b);
        });
      } else {
        const ring = new THREE.Mesh(
          new THREE.TorusGeometry(caseR * 0.985, 0.036 * scale, 20, 128),
          polishedMetal(k.metal > 0.5 ? caseColor : 0xc8ccd4, 0.14));
        ring.rotation.x = Math.PI / 2;
        ring.position.y = -0.008 * scale;
        add(ring);
      }
    }

    /* ── dial ──────────────────────────────────────────────────────────
       Self-luminous: base colour black so only the emissive map shows, which is
       what an always-on panel looks like under a crystal. */
    const face = blankCanvas(1024);
    this.drawFace(face, cfg);
    const tex = new THREE.CanvasTexture(face);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 8;
    const bright = (d.id === 'd3' || d.id === 'd4' || d.id === 'd5') ? 1.45
      : d.id === 'd2' ? 1.15 : 0.85;
    const screenMat = new THREE.MeshStandardMaterial({
      color: 0x000000, emissive: 0xffffff, emissiveMap: tex, emissiveIntensity: bright,
      roughness: 0.30, metalness: 0,
    });

    let screenGeo;
    if (square) {
      screenGeo = new THREE.ShapeGeometry(squircleShape(dialR, 4.6, 128), 12);
      // ShapeGeometry hands back shape-space UVs; renormalise so the dial fits 0..1
      const uv = screenGeo.attributes.uv;
      for (let i = 0; i < uv.count; i++) {
        uv.setXY(i, uv.getX(i) / (2 * dialR) + 0.5, uv.getY(i) / (2 * dialR) + 0.5);
      }
      uv.needsUpdate = true;
      screenGeo.rotateX(-Math.PI / 2);
    } else {
      screenGeo = new THREE.CircleGeometry(dialR * 0.985, 128);
      screenGeo.rotateX(-Math.PI / 2);
    }
    const screen = new THREE.Mesh(screenGeo, screenMat);
    screen.position.y = screenY;
    grp.add(screen);   // a light source, not a shadow caster

    /* ── cover crystal ── */
    const domed = !square && (st.id === 'classic' || st.id === 'luxury');
    let glassGeo;
    if (square) {
      glassGeo = new THREE.ShapeGeometry(squircleShape(dialR * 1.015, 4.6, 128), 12);
      glassGeo.rotateX(-Math.PI / 2);
    } else if (domed) {
      // a shallow box crystal: the give-away detail on a dress watch
      const gp = [], R = dialR * 1.02, dome = 0.075 * scale;
      for (let i = 0; i <= 16; i++) {
        const a = (i / 16) * (Math.PI / 2);
        gp.push(new THREE.Vector2(R * Math.sin(a), dome * Math.cos(a)));
      }
      glassGeo = new THREE.LatheGeometry(gp, 128);
    } else {
      glassGeo = new THREE.CircleGeometry(dialR * 1.02, 128);
      glassGeo.rotateX(-Math.PI / 2);
    }
    const glass = new THREE.Mesh(glassGeo, glassMaterial(d.id));
    glass.position.y = glassY;
    glass.renderOrder = 3;
    grp.add(glass);

    /* ── crown and buttons ── */
    const metalCol = st.id === 'luxury' ? GOLD : (k.metal > 0.5 ? caseColor : 0x9aa3ad);
    const crownMat = polishedMetal(metalCol, 0.20);
    const cr = ((st.id === 'luxury' || st.id === 'classic') ? 0.155 : 0.135) * scale;
    const crownPos = new THREE.Vector3(caseR * 1.04, crownY, -0.30 * scale);
    const crown = new THREE.Mesh(new THREE.CylinderGeometry(cr, cr, 0.21 * scale, 32), crownMat);
    crown.rotation.z = Math.PI / 2;
    crown.position.copy(crownPos);
    add(crown);
    {
      const N = 26;
      const teeth = new THREE.InstancedMesh(
        new THREE.BoxGeometry(0.20 * scale, 0.016 * scale, 0.030 * scale), crownMat, N);
      const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), one = new THREE.Vector3(1, 1, 1);
      for (let i = 0; i < N; i++) {
        const a = (i / N) * Math.PI * 2;
        q.setFromAxisAngle(X, a);
        m4.compose(new THREE.Vector3(
          crownPos.x, crownPos.y + Math.cos(a) * cr * 0.99, crownPos.z + Math.sin(a) * cr * 0.99), q, one);
        teeth.setMatrixAt(i, m4);
      }
      teeth.castShadow = true;
      grp.add(teeth);
    }
    if (st.id === 'sporty') {
      for (const zz of [0.30, -0.02]) {
        const b = new THREE.Mesh(
          roundedBox(0.10 * scale, 0.105 * scale, 0.30 * scale, 0.032 * scale), crownMat);
        b.position.set(caseR * 1.02, crownY, zz * scale);
        add(b);
      }
    } else if (square) {
      const b = new THREE.Mesh(
        roundedBox(0.085 * scale, 0.12 * scale, 0.50 * scale, 0.035 * scale), crownMat);
      b.position.set(caseR * 0.99, crownY - 0.02 * scale, 0.22 * scale);
      add(b);
    }

    /* ── caseback sensor cluster ── */
    const puckR = 0.40 * scale;
    const puck = new THREE.Mesh(
      new THREE.CylinderGeometry(puckR, puckR * 0.94, 0.05 * scale, 48),
      new THREE.MeshPhysicalMaterial({
        color: 0x0a0d13, metalness: 0.1, roughness: 0.16,
        clearcoat: 1, clearcoatRoughness: 0.06, envMapIntensity: 1.2,
      }));
    puck.position.y = caseBottomY + 0.02 * scale;
    add(puck);
    if ((cfg.sensors || []).includes('hr')) {
      const lensMat = new THREE.MeshStandardMaterial({
        color: 0x0d2c19, emissive: 0x2bd46b, emissiveIntensity: 1.6, roughness: 0.25,
      });
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2 + 0.4;
        const led = new THREE.Mesh(new THREE.CircleGeometry(0.052 * scale, 20), lensMat);
        led.rotation.x = Math.PI / 2;
        led.position.set(Math.cos(a) * puckR * 0.5, caseBottomY - 0.006 * scale, Math.sin(a) * puckR * 0.5);
        grp.add(led);
      }
    }

    /* ── lugs and spring bars ── */
    const lugZ = caseR * (square ? 0.86 : 0.90);
    const lugW = (square ? 1.20 : 0.92) * scale;
    for (const dir of [1, -1]) {
      const lug = new THREE.Mesh(
        roundedBox(lugW, 0.20 * scale, 0.44 * scale, 0.07 * scale), caseMat);
      lug.position.set(0, -caseH * 0.42, dir * lugZ);
      lug.rotation.x = dir * 0.22;
      add(lug);
      const bar = new THREE.Mesh(
        new THREE.CylinderGeometry(0.032 * scale, 0.032 * scale, lugW * 0.98, 16),
        polishedMetal(0xb9bec7, 0.24));
      bar.rotation.z = Math.PI / 2;
      bar.position.set(0, -caseH * 0.46, dir * (lugZ + 0.14 * scale));
      add(bar);
    }

    /* ── straps ── */
    const strapMat = strapMaterial(s, cfg);
    const z0 = caseR * 0.95;
    const hw0 = (s.id === 's2' ? 0.44 : 0.47) * scale;
    const hw1 = (s.id === 's3' ? 0.33 : s.id === 's2' ? 0.42 : 0.37) * scale;
    const th0 = (s.id === 's2' ? 0.052 : s.id === 's3' ? 0.070 : 0.078) * scale;
    const th1 = th0 * 0.78;
    const sectionN = s.id === 's1' ? 3.4 : s.id === 's2' ? 6 : 4.6;
    const STEPS = 96;

    for (const dir of [1, -1]) {
      const curve = new THREE.CatmullRomCurve3([
        new THREE.Vector3(0, -caseH * 0.44, dir * z0),
        new THREE.Vector3(0, -caseH * 0.70, dir * (z0 + 0.44 * scale)),
        new THREE.Vector3(0, -0.66 * scale, dir * (z0 + 0.94 * scale)),
        new THREE.Vector3(0, -1.34 * scale, dir * (z0 + 0.96 * scale)),
        new THREE.Vector3(0, -1.84 * scale, dir * (z0 + 0.54 * scale)),
        new THREE.Vector3(0, -2.06 * scale, dir * (z0 - 0.04 * scale)),
      ], false, 'catmullrom', 0.5);

      if (s.id === 's4') {
        /* Three-row link bracelet: a wide centre link flanked by two narrower ones,
           each seated on the curve and turned to follow the tangent. */
        const LINKS = 13;
        const P = new THREE.Vector3(), T = new THREE.Vector3(), NM = new THREE.Vector3();
        const m4 = new THREE.Matrix4();
        const rows = [{ off: 0, w: 0.42 }, { off: 0.34, w: 0.28 }, { off: -0.34, w: 0.28 }];
        rows.forEach((r) => {
          const geo = roundedBox(r.w * 2 * scale, 0.072 * scale, 0.20 * scale, 0.026 * scale);
          const inst = new THREE.InstancedMesh(geo, strapMat, LINKS);
          for (let i = 0; i < LINKS; i++) {
            const t = (i + 0.5) / LINKS;
            curve.getPointAt(t, P);
            curve.getTangentAt(t, T);
            NM.crossVectors(T, X).normalize();
            const taper = 1 - 0.22 * t;
            // geometry axes: width→X, thickness→strap normal, depth→tangent
            m4.compose(
              new THREE.Vector3(P.x + r.off * 2 * scale * taper, P.y, P.z),
              basisQuat(X, NM),
              new THREE.Vector3(taper, 1, 1));
            inst.setMatrixAt(i, m4);
          }
          inst.castShadow = true;
          inst.receiveShadow = true;
          grp.add(inst);
        });
        continue;
      }

      const { geometry, frames } = sweep(
        curve, STEPS, beltSection(sectionN, 40),
        (t) => hw0 + (hw1 - hw0) * Math.pow(t, 1.15),
        (t) => th0 + (th1 - th0) * t,
        9);
      add(new THREE.Mesh(geometry, strapMat));
      const frameAt = (t) => frames[Math.max(0, Math.min(STEPS, Math.round(t * STEPS)))];

      /* Leather gets a stitched edge; the sport strap gets adjustment holes. */
      if (s.id === 's3') {
        const COUNT = 34;
        const inst = new THREE.InstancedMesh(
          new THREE.CapsuleGeometry(0.016 * scale, 0.052 * scale, 3, 8),
          new THREE.MeshStandardMaterial({ color: 0xd9cdb4, roughness: 0.75, metalness: 0 }),
          COUNT * 2);
        const q = new THREE.Quaternion(), m4 = new THREE.Matrix4(), one = new THREE.Vector3(1, 1, 1);
        let n = 0;
        for (let i = 0; i < COUNT; i++) {
          const f = frameAt(0.06 + (i / (COUNT - 1)) * 0.86);
          q.setFromUnitVectors(UP, f.t);
          for (const sgn of [1, -1]) {
            m4.compose(
              f.p.clone().addScaledVector(X, sgn * f.w * 0.74).addScaledVector(f.n, f.th * 0.88),
              q, one);
            inst.setMatrixAt(n++, m4);
          }
        }
        inst.castShadow = true;
        grp.add(inst);
      } else if (dir === -1) {
        const holeMat = new THREE.MeshStandardMaterial({ color: 0x05070a, roughness: 0.9, metalness: 0 });
        for (let i = 0; i < 5; i++) {
          const f = frameAt(0.55 + i * 0.075);
          const hole = new THREE.Mesh(
            new THREE.CylinderGeometry(0.048 * scale, 0.048 * scale, f.th * 2.6, 18), holeMat);
          hole.quaternion.setFromUnitVectors(UP, f.n);
          hole.position.copy(f.p);
          grp.add(hole);
        }
      }

      /* A real pin buckle on the +z side. */
      if (dir === 1) {
        const f = frameAt(0.70);
        const bw = f.w * 2.35, bh = 0.46 * scale, bar = 0.055 * scale;
        const sh = roundedRect(bw, bh, 0.07 * scale);
        sh.holes.push(roundedRect(bw - 2 * bar, bh - 2 * bar, 0.04 * scale, true));
        const bGeo = new THREE.ExtrudeGeometry(sh, {
          depth: bar, bevelEnabled: true, bevelThickness: 0.014 * scale,
          bevelSize: 0.014 * scale, bevelSegments: 2, curveSegments: 10,
        });
        bGeo.center();
        const buckleMat = polishedMetal(st.id === 'luxury' ? GOLD : 0xb9bec7, 0.20);
        const buckle = new THREE.Mesh(bGeo, buckleMat);
        // geometry axes: width→X, length→tangent, thickness→derived third axis
        buckle.quaternion.copy(basisQuat(X, f.t));
        buckle.position.copy(f.p).addScaledVector(f.n, f.th * 0.6);
        add(buckle);
        const tongue = new THREE.Mesh(
          new THREE.CylinderGeometry(0.022 * scale, 0.016 * scale, bw * 0.92, 12), buckleMat);
        tongue.quaternion.setFromUnitVectors(UP, X);
        tongue.position.copy(buckle.position).addScaledVector(f.t, -bh * 0.22);
        add(tongue);
      }
    }

    grp.rotation.x = TUNE.tilt.x;
    grp.rotation.z = TUNE.tilt.z;
    grp.userData.accent = accNum;
    return grp;
  },

  /* ═══════════════════ 7 · the dial ═══════════════════
     Per-brand identity, unchanged from the prototype: each manufacturer gets a
     deterministic layout + accent from a hash of its brand seed, so two firms never
     look the same, and the dial adapts to which sensors are fitted. The drawing
     itself now runs on a 1024px canvas (the coordinate system is still the original
     256px one, scaled up) with accent glow, so it stands up to a close crop. */

  _brandHash(str) {
    str = String(str == null ? 'x' : str);
    let h = 2166136261 >>> 0;
    for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
    return h >>> 0;
  },
  faceTheme(cfg) {
    const seed = cfg.brand != null ? cfg.brand : (cfg.style + '_' + cfg.colorHex + '_' + cfg.display);
    const h = this._brandHash(seed);
    const PAL = [0x22d3ee, 0x34d399, 0xa78bfa, 0xfb923c, 0xf472b6, 0x60a5fa, 0xfbbf24, 0x4ade80, 0xf87171, 0x2dd4bf];
    return { layout: h % 6, accent: PAL[Math.floor(h / 6) % PAL.length], h };
  },
  _rr(fx, x, y, w, h, r) {
    fx.beginPath(); fx.moveTo(x + r, y);
    fx.arcTo(x + w, y, x + w, y + h, r); fx.arcTo(x + w, y + h, x, y + h, r);
    fx.arcTo(x, y + h, x, y, r); fx.arcTo(x, y, x + w, y, r); fx.closePath();
  },

  drawFace(canvas, cfg) {
    const fx = canvas.getContext('2d');
    const S = canvas.width / 256;            // author at 256, render at whatever we got
    const W = 256, H = 256, cx = 128, cy = 128;
    fx.save();
    fx.scale(S, S);

    const st = COMPONENTS.style.find((x) => x.id === cfg.style) || COMPONENTS.style[2];
    const lcd = cfg.display === 'd1';
    const theme = this.faceTheme(cfg);
    const hx = (n) => '#' + (n >>> 0).toString(16).padStart(6, '0').slice(-6);
    const acc = hx(theme.accent);
    const glow = (on, color, blur) => {
      fx.shadowColor = on ? (color || acc) : 'transparent';
      fx.shadowBlur = on ? (blur || 9) : 0;
    };

    // backdrop — LCD panels read flatter and cooler than OLED/AMOLED
    fx.clearRect(0, 0, W, H);
    fx.fillStyle = lcd ? '#0a0c10' : '#04060d'; fx.fillRect(0, 0, W, H);
    const grad = fx.createRadialGradient(cx, 92, 6, cx, cy, 180);
    grad.addColorStop(0, lcd ? '#141a24' : '#0c1a36');
    grad.addColorStop(1, lcd ? '#070a0f' : '#03050b');
    fx.fillStyle = grad; fx.fillRect(0, 0, W, H);

    // sensor-driven readouts
    const has = (id) => (cfg.sensors || []).includes(id);
    const M = [];
    if (has('hr')) M.push({ ic: '♥', v: '72', u: 'BPM', c: '#fb7185' });
    if (has('spo2')) M.push({ ic: 'O₂', v: '98', u: '%', c: '#60a5fa' });
    if (has('gps')) M.push({ ic: '◎', v: '5.2', u: 'KM', c: '#34d399' });
    if (has('slp')) M.push({ ic: '☾', v: '7:20', u: 'SLP', c: '#a78bfa' });
    if (has('ecg')) M.push({ ic: '∿', v: 'SR', u: 'ECG', c: '#f472b6' });
    if (has('tmp')) M.push({ ic: '°', v: '36.6', u: '°C', c: '#fbbf24' });
    if (has('bp')) M.push({ ic: '◇', v: '120', u: '/80', c: '#f87171' });
    if (has('glu')) M.push({ ic: '≈', v: '5.4', u: 'mmol', c: '#4ade80' });

    const dim = lcd ? '#6b7687' : '#8b96b8';
    const ink = lcd ? '#c8d2dd' : '#eef2fb';
    const time = '10:09', date = 'FRI 12', L = theme.layout;
    fx.textAlign = 'center'; fx.textBaseline = 'alphabetic';

    if (L === 0) {
      // Digital Sport — bold time, accent arc, metric pills
      glow(true, acc, 12);
      fx.strokeStyle = acc; fx.lineWidth = 6; fx.lineCap = 'round';
      fx.beginPath(); fx.arc(cx, cy, 98, -Math.PI * 0.80, -Math.PI * 0.20); fx.stroke();
      fx.lineCap = 'butt'; glow(false);
      fx.fillStyle = ink; fx.font = '800 60px Arial'; fx.fillText(time, cx, 116);
      glow(true, acc, 8);
      fx.fillStyle = acc; fx.font = '700 15px Arial'; fx.fillText(date, cx, 140);
      glow(false);
      const top = M.slice(0, 3), n = top.length;
      if (n) {
        const bw = 64, gap = 8, totW = n * bw + (n - 1) * gap, x0 = cx - totW / 2;
        for (let i = 0; i < n; i++) {
          const m = top[i], x = x0 + i * (bw + gap);
          fx.fillStyle = 'rgba(255,255,255,.05)'; this._rr(fx, x, 166, bw, 42, 9); fx.fill();
          glow(true, m.c, 7);
          fx.fillStyle = m.c; fx.font = '700 18px Arial'; fx.fillText(m.v, x + bw / 2, 190);
          glow(false);
          fx.fillStyle = dim; fx.font = '600 10px Arial'; fx.fillText(m.ic + ' ' + m.u, x + bw / 2, 203);
        }
      }
    } else if (L === 1) {
      // Analog Classic — hour markers, hands, sub-dials, serif type
      for (let i = 0; i < 12; i++) {
        const a = (i / 12) * Math.PI * 2, r1 = 94, r2 = (i % 3 === 0 ? 80 : 87);
        fx.strokeStyle = (i % 3 === 0 ? ink : dim);
        fx.lineWidth = (i % 3 === 0 ? 4 : 2);
        fx.beginPath();
        fx.moveTo(cx + Math.cos(a) * r1, cy + Math.sin(a) * r1);
        fx.lineTo(cx + Math.cos(a) * r2, cy + Math.sin(a) * r2);
        fx.stroke();
      }
      fx.fillStyle = dim; fx.font = 'italic 600 13px Georgia,serif'; fx.fillText('AUTOMATIC', cx, 92);
      fx.fillStyle = 'rgba(255,255,255,.08)'; this._rr(fx, cx + 42, cy - 13, 30, 26, 4); fx.fill();
      fx.fillStyle = ink; fx.font = '700 15px Georgia,serif'; fx.fillText('12', cx + 57, cy + 6);
      const hands = [
        [-Math.PI / 2 + (10 / 12) * 2 * Math.PI + (9 / 60) * (Math.PI / 6), 50, 5, ink],
        [-Math.PI / 2 + (9 / 60) * 2 * Math.PI, 72, 3, ink],
        [-Math.PI / 2 + (44 / 60) * 2 * Math.PI, 82, 1.6, acc],
      ];
      fx.lineCap = 'round';
      hands.forEach((h) => {
        glow(h[3] === acc, acc, 8);
        fx.strokeStyle = h[3]; fx.lineWidth = h[2];
        fx.beginPath(); fx.moveTo(cx, cy);
        fx.lineTo(cx + Math.cos(h[0]) * h[1], cy + Math.sin(h[0]) * h[1]);
        fx.stroke();
      });
      glow(false);
      fx.lineCap = 'butt';
      fx.fillStyle = acc; fx.beginPath(); fx.arc(cx, cy, 5, 0, 7); fx.fill();
      const sd = M.slice(0, 2), pos = [[cx, cy + 48], [cx - 46, cy]];
      sd.forEach((m, i) => {
        const px = pos[i][0], py = pos[i][1];
        fx.strokeStyle = dim; fx.lineWidth = 2;
        fx.beginPath(); fx.arc(px, py, 18, 0, 7); fx.stroke();
        fx.fillStyle = m.c; fx.font = '700 12px Georgia,serif'; fx.fillText(m.v, px, py + 4);
      });
    } else if (L === 2) {
      // Modern Grid — header time + tile complications
      fx.textAlign = 'left';
      fx.fillStyle = ink; fx.font = '600 30px "Segoe UI",Arial'; fx.fillText(time, 30, 64);
      glow(true, acc, 7);
      fx.fillStyle = acc; fx.font = '600 12px "Segoe UI",Arial'; fx.fillText(date, 32, 82);
      glow(false);
      const tiles = M.slice(0, 4);
      if (tiles.length) {
        const gw = 78, gh = 44, gx = cx - gw - 5, gy = 98;
        tiles.forEach((m, i) => {
          const x = gx + (i % 2) * (gw + 10), y = gy + Math.floor(i / 2) * (gh + 10);
          fx.fillStyle = m.c + '22'; this._rr(fx, x, y, gw, gh, 10); fx.fill();
          fx.fillStyle = m.c; fx.font = '700 19px "Segoe UI",Arial'; fx.fillText(m.v, x + 10, y + 27);
          fx.fillStyle = dim; fx.font = '600 10px "Segoe UI",Arial'; fx.fillText(m.ic + ' ' + m.u, x + 10, y + 39);
        });
      } else {
        fx.fillStyle = dim; fx.font = '600 14px "Segoe UI",Arial'; fx.fillText('— no sensors —', 30, cy + 30);
      }
      fx.textAlign = 'center';
    } else if (L === 3) {
      // Minimal Lume — thin type, single accent ring, airy
      fx.lineWidth = 3; fx.strokeStyle = 'rgba(255,255,255,.07)';
      fx.beginPath(); fx.arc(cx, cy, 104, 0, 7); fx.stroke();
      glow(true, acc, 10);
      fx.strokeStyle = acc; fx.lineWidth = 3; fx.lineCap = 'round';
      fx.beginPath(); fx.arc(cx, cy, 104, -Math.PI / 2, -Math.PI / 2 + Math.PI * 1.1); fx.stroke();
      fx.lineCap = 'butt'; glow(false);
      fx.fillStyle = ink; fx.font = '200 70px "Helvetica Neue",Arial'; fx.fillText(time, cx, cy + 10);
      fx.fillStyle = dim; fx.font = '500 13px "Helvetica Neue",Arial'; fx.fillText(date, cx, cy + 36);
      if (M[0]) {
        glow(true, M[0].c, 8);
        fx.fillStyle = M[0].c; fx.font = '600 15px "Helvetica Neue",Arial';
        fx.fillText(M[0].ic + ' ' + M[0].v + ' ' + M[0].u, cx, 72);
        glow(false);
      }
    } else if (L === 4) {
      // Infograph Arc — concentric perimeter rings, one per sensor
      const arcs = M.slice(0, 4), frac = [0.7, 0.85, 0.55, 0.9];
      fx.lineCap = 'round';
      arcs.forEach((m, i) => {
        const rr = 102 - i * 15;
        fx.strokeStyle = 'rgba(255,255,255,.06)'; fx.lineWidth = 7;
        fx.beginPath(); fx.arc(cx, cy, rr, 0, 7); fx.stroke();
        glow(true, m.c, 8);
        fx.strokeStyle = m.c; fx.lineWidth = 7;
        fx.beginPath(); fx.arc(cx, cy, rr, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * frac[i]); fx.stroke();
        glow(false);
      });
      fx.lineCap = 'butt';
      fx.fillStyle = ink; fx.font = '700 40px "Segoe UI",Arial'; fx.fillText(time, cx, cy + 4);
      fx.fillStyle = acc; fx.font = '600 12px "Segoe UI",Arial'; fx.fillText(date, cx, cy + 26);
      if (arcs[0]) {
        fx.fillStyle = arcs[0].c; fx.font = '600 12px "Segoe UI",Arial';
        fx.fillText(arcs[0].ic + ' ' + arcs[0].v, cx, cy - 24);
      }
    } else {
      // Mono LCD — segmented, single-accent, budget look
      const mono = 'Consolas,"Courier New",monospace';
      fx.fillStyle = lcd ? '#10151c' : '#0a1018'; this._rr(fx, 26, 42, 204, 150, 12); fx.fill();
      glow(true, acc, lcd ? 4 : 10);
      fx.fillStyle = acc; fx.font = '700 52px ' + mono; fx.fillText(time, cx, 110);
      glow(false);
      fx.fillStyle = dim; fx.font = '700 14px ' + mono; fx.fillText(date, cx, 134);
      const rows = M.slice(0, 3);
      fx.textAlign = 'left'; fx.font = '700 14px ' + mono;
      rows.forEach((m, i) => {
        const y = 160 + i * 18;
        fx.fillStyle = dim; fx.fillText(m.ic, 54, y);
        fx.fillStyle = m.c; fx.fillText(m.v + ' ' + m.u, 80, y);
      });
      fx.textAlign = 'center';
    }

    // panel vignette — every real display falls off toward the bezel
    const vg = fx.createRadialGradient(cx, cy, 52, cx, cy, 168);
    vg.addColorStop(0, 'rgba(0,0,0,0)');
    vg.addColorStop(1, 'rgba(0,0,0,0.5)');
    fx.fillStyle = vg; fx.fillRect(0, 0, W, H);
    fx.restore();
  },

  /* ═══════════════════ 8 · live view ═══════════════════ */

  _disposeTree(root) {
    const MAPS = ['map', 'emissiveMap', 'normalMap', 'roughnessMap', 'metalnessMap',
      'aoMap', 'alphaMap', 'clearcoatNormalMap', 'clearcoatRoughnessMap',
      'sheenColorMap', 'sheenRoughnessMap', 'anisotropyMap', 'iridescenceMap'];
    root.traverse((o) => {
      if (o.geometry) o.geometry.dispose();
      const mats = Array.isArray(o.material) ? o.material : (o.material ? [o.material] : []);
      for (const m of mats) {
        for (const key of MAPS) if (m[key] && m[key].dispose) m[key].dispose();
        m.dispose();
      }
    });
  },

  /**
   * Mount an interactive view on `canvas`.
   * opts: { autoSpin, glow, exposure, shadowOpacity, onReady }
   */
  mountLive(canvas, cfg, opts = {}) {
    this.unmountLive();
    if (!this.hasGL()) return null;

    const renderer = new THREE.WebGLRenderer({
      canvas, antialias: true, alpha: true, powerPreference: 'high-performance',
    });
    renderer.setPixelRatio(Math.min(TUNE.dpr, window.devicePixelRatio || 1));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = opts.exposure != null ? opts.exposure : TUNE.exposure;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    const scene = new THREE.Scene();
    scene.environment = studioEnv(renderer);

    const camera = new THREE.PerspectiveCamera(TUNE.fov, 1, 0.1, 100);
    camera.position.set(TUNE.camera[0], TUNE.camera[1], TUNE.camera[2]);

    // The IBL does the shading; this one exists mainly to cast a directional shadow.
    const key = new THREE.DirectionalLight(0xffffff, 1.05);
    key.position.set(3.2, 8.4, 4.4);
    key.castShadow = true;
    key.shadow.mapSize.set(TUNE.shadowMap, TUNE.shadowMap);
    key.shadow.bias = -0.0006;
    key.shadow.normalBias = 0.022;
    key.shadow.radius = 5;
    const sc = key.shadow.camera;
    sc.left = -3.4; sc.right = 3.4; sc.top = 3.4; sc.bottom = -3.4;
    sc.near = 1; sc.far = 24; sc.updateProjectionMatrix();
    scene.add(key);

    const group = this.buildWatch(cfg);
    const pivot = new THREE.Group();
    pivot.add(group);
    scene.add(pivot);

    /* Stage: a shadow catcher just under the lowest point of the piece, plus an
       ambient contact pool, plus an accent backlight that reads on dark panels. */
    const bb = new THREE.Box3().setFromObject(group);
    const groundY = bb.min.y - 0.05;

    const catcher = new THREE.Mesh(
      new THREE.PlaneGeometry(26, 26),
      new THREE.ShadowMaterial({ opacity: opts.shadowOpacity != null ? opts.shadowOpacity : 0.42 }));
    catcher.rotation.x = -Math.PI / 2;
    catcher.position.y = groundY;
    catcher.receiveShadow = true;
    scene.add(catcher);

    const pool = new THREE.Mesh(
      new THREE.PlaneGeometry(6.5, 6.5),
      new THREE.MeshBasicMaterial({
        map: TEX.radial(0.05, 2.0), color: 0x000000, transparent: true,
        opacity: 0.34, depthWrite: false, toneMapped: false,
      }));
    pool.rotation.x = -Math.PI / 2;
    pool.position.y = groundY + 0.004;
    scene.add(pool);

    let glowMesh = null;
    if (opts.glow !== false) {
      glowMesh = new THREE.Mesh(
        new THREE.PlaneGeometry(9, 9),
        new THREE.MeshBasicMaterial({
          map: TEX.radial(0, 2.4), color: new THREE.Color(group.userData.accent),
          transparent: true, opacity: 0.20, depthWrite: false, depthTest: false,
          blending: THREE.AdditiveBlending, toneMapped: false,
        }));
      glowMesh.position.set(0, 0.15, -2.8);
      glowMesh.renderOrder = -1;
      scene.add(glowMesh);
    }

    const state = {
      renderer, scene, camera, group, pivot, canvas, catcher, pool, glow: glowMesh, key,
      rotY: -0.42, base: -0.42, sway: 0, rotVel: 0, tiltX: 0, px: 0, py: 0, pxT: 0, pyT: 0,
      dragging: false, lastX: 0, lastY: 0,
      autoSpin: opts.autoSpin !== false, dolly: 1,
      born: (typeof performance !== 'undefined' ? performance.now() : Date.now()),
      last: 0, visible: true, onScreen: true, raf: 0, opts,
    };

    /* ── sizing ── */
    const resize = () => {
      const w = Math.max(1, canvas.clientWidth || canvas.width || 1);
      const h = Math.max(1, canvas.clientHeight || canvas.height || 1);
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      state.dolly = Math.min(1.7, Math.max(1, 1.18 / Math.max(0.6, camera.aspect)));
    };
    resize();
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(resize);
      ro.observe(canvas);
    } else {
      window.addEventListener('resize', resize);
    }

    /* ── input ── */
    const pt = (e) => (e.touches && e.touches[0]) ? e.touches[0] : e;
    const onDown = (e) => {
      const p = pt(e);
      state.dragging = true; state.rotVel = 0;
      state.lastX = p.clientX; state.lastY = p.clientY;
      canvas.style.cursor = 'grabbing';
    };
    const onMove = (e) => {
      const p = pt(e);
      if (state.dragging) {
        const dx = (p.clientX - state.lastX) * 0.0105;
        const dy = (p.clientY - state.lastY) * 0.0060;
        state.rotY += dx;
        state.rotVel = dx * 0.45;
        state.tiltX = Math.max(-0.5, Math.min(0.5, state.tiltX + dy));
        state.lastX = p.clientX; state.lastY = p.clientY;
      } else if (e.clientX != null) {
        // gentle pointer parallax while idle
        const r = canvas.getBoundingClientRect();
        state.pxT = ((e.clientX - r.left) / Math.max(1, r.width) - 0.5) * 2;
        state.pyT = ((e.clientY - r.top) / Math.max(1, r.height) - 0.5) * 2;
      }
    };
    const onUp = () => {
      if (state.dragging) { state.base = state.rotY; state.sway = 0; }
      state.dragging = false;
      canvas.style.cursor = 'grab';
    };
    const onLeave = () => { state.pxT = 0; state.pyT = 0; };

    canvas.style.cursor = 'grab';
    canvas.style.touchAction = 'pan-y';
    canvas.addEventListener('mousedown', onDown);
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);
    window.addEventListener('mouseup', onUp);
    canvas.addEventListener('touchstart', onDown, { passive: true });
    canvas.addEventListener('touchmove', onMove, { passive: true });
    canvas.addEventListener('touchend', onUp);

    /* ── don't burn a GPU on a watch nobody is looking at ── */
    const onVis = () => { state.visible = !document.hidden; };
    document.addEventListener('visibilitychange', onVis);
    let io = null;
    if (typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver(
        (es) => { state.onScreen = es.some((x) => x.isIntersecting); },
        { threshold: 0 });
      io.observe(canvas);
    }

    /* ── frame loop ── */
    const target = new THREE.Vector3(TUNE.target[0], TUNE.target[1], TUNE.target[2]);
    const loop = () => {
      state.raf = requestAnimationFrame(loop);
      if (!state.visible || !state.onScreen) { state.last = 0; return; }
      const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
      const dt = state.last ? Math.min(3, (now - state.last) / 16.667) : 1;
      state.last = now;

      if (!state.dragging) {
        state.rotY += state.rotVel * dt;
        state.rotVel *= Math.pow(0.90, dt);
        if (state.autoSpin && Math.abs(state.rotVel) < 0.0007) {
          state.sway += dt;
          const target = state.base + Math.sin(state.sway * TUNE.swayRate) * TUNE.sway;
          state.rotY += (target - state.rotY) * 0.035 * dt;
        }
        state.tiltX *= Math.pow(0.985, dt);     // settle back to the resting pose
      }
      state.px += (state.pxT - state.px) * 0.06 * dt;
      state.py += (state.pyT - state.py) * 0.06 * dt;

      // entrance: ease up to full size and opacity over ~850ms
      const age = Math.min(1, (now - state.born) / 850);
      const e = 1 - Math.pow(1 - age, 3);
      canvas.style.opacity = String(0.25 + 0.75 * e);

      state.pivot.rotation.y = state.rotY;
      state.group.rotation.x = TUNE.tilt.x + state.tiltX;
      state.group.scale.setScalar(0.955 + 0.045 * e);

      const d = state.dolly;
      camera.position.set(
        target.x + (TUNE.camera[0] + state.px * 0.55 - target.x) * d,
        target.y + (TUNE.camera[1] - state.py * 0.40 - target.y) * d,
        target.z + (TUNE.camera[2] - target.z) * d);
      camera.lookAt(target);

      if (state.glow) state.glow.quaternion.copy(camera.quaternion);
      renderer.render(scene, camera);
    };
    state._cleanup = () => {
      if (ro) ro.disconnect(); else window.removeEventListener('resize', resize);
      if (io) io.disconnect();
      document.removeEventListener('visibilitychange', onVis);
      canvas.removeEventListener('mousedown', onDown);
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
      window.removeEventListener('mouseup', onUp);
      canvas.removeEventListener('touchstart', onDown);
      canvas.removeEventListener('touchmove', onMove);
      canvas.removeEventListener('touchend', onUp);
    };

    this.live = state;
    loop();
    if (opts.onReady) opts.onReady(state);
    return state;
  },

  updateLive(cfg) {
    if (!this.live) return;
    const st = this.live;
    st.pivot.remove(st.group);
    this._disposeTree(st.group);
    st.group = this.buildWatch(cfg);
    st.pivot.add(st.group);
    if (st.glow) st.glow.material.color.set(st.group.userData.accent);
    // keep the stage glued to the new silhouette (sizes and styles differ in height)
    const bb = new THREE.Box3().setFromObject(st.group);
    const groundY = bb.min.y - 0.05;
    st.catcher.position.y = groundY;
    st.pool.position.y = groundY + 0.004;
  },

  unmountLive() {
    if (!this.live) return;
    const st = this.live;
    cancelAnimationFrame(st.raf);
    if (st._cleanup) st._cleanup();
    this._disposeTree(st.group);
    this._disposeTree(st.catcher);
    this._disposeTree(st.pool);
    if (st.glow) this._disposeTree(st.glow);
    st.renderer.dispose();
    this.live = null;
  },

  /* ═══════════════════ 9 · offscreen snapshots ═══════════════════
     Product thumbnails run through the same pipeline, so a catalog tile and the
     studio view are the same watch under the same light. */

  _snapCache: {},
  // graceful placeholder when WebGL is unavailable (e.g. a CDN-blocking extension)
  _ph: 'data:image/svg+xml;utf8,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64"><rect width="64" height="64" rx="12" fill="#0c1228"/><rect x="25" y="12" width="14" height="40" rx="5" fill="#39435c"/><circle cx="32" cy="32" r="13" fill="#141b30" stroke="#22d3ee" stroke-width="2"/><circle cx="32" cy="32" r="2" fill="#22d3ee"/></svg>'),

  snapshot(cfg, key) {
    if (key && this._snapCache[key]) return this._snapCache[key];
    if (!this.hasGL()) return this._ph;
    if (!this._snap) {
      const canvas = document.getElementById('snap-canvas') || document.createElement('canvas');
      const renderer = new THREE.WebGLRenderer({
        canvas, antialias: true, alpha: true, preserveDrawingBuffer: true,
      });
      renderer.setSize(512, 512, false);
      renderer.setPixelRatio(1);
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = TUNE.exposure;
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;

      const scene = new THREE.Scene();
      scene.environment = studioEnv(renderer);
      const camera = new THREE.PerspectiveCamera(TUNE.fov, 1, 0.1, 100);
      camera.position.set(1.7, 4.0, 6.8);
      camera.lookAt(0, -0.3, 0);

      const kl = new THREE.DirectionalLight(0xffffff, 1.05);
      kl.position.set(3.2, 8.4, 4.4);
      kl.castShadow = true;
      kl.shadow.mapSize.set(1024, 1024);
      kl.shadow.bias = -0.0006;
      kl.shadow.normalBias = 0.022;
      const c = kl.shadow.camera;
      c.left = -3.4; c.right = 3.4; c.top = 3.4; c.bottom = -3.4; c.near = 1; c.far = 24;
      c.updateProjectionMatrix();
      scene.add(kl);

      const catcher = new THREE.Mesh(
        new THREE.PlaneGeometry(26, 26), new THREE.ShadowMaterial({ opacity: 0.34 }));
      catcher.rotation.x = -Math.PI / 2;
      catcher.receiveShadow = true;
      scene.add(catcher);

      this._snap = { renderer, scene, camera, catcher };
    }
    const grp = this.buildWatch(cfg);
    const pivot = new THREE.Group();
    pivot.rotation.y = -0.5;
    pivot.add(grp);
    this._snap.scene.add(pivot);
    pivot.updateMatrixWorld(true);
    this._snap.catcher.position.y = new THREE.Box3().setFromObject(grp).min.y - 0.05;
    this._snap.renderer.render(this._snap.scene, this._snap.camera);
    const url = this._snap.renderer.domElement.toDataURL('image/png');
    this._snap.scene.remove(pivot);
    this._disposeTree(grp);
    if (key) this._snapCache[key] = url;
    return url;
  },

  prodCfg(spec, brand) {
    const col = COMPONENTS.colors.find((c) => c.id === spec.color);
    return {
      casing: spec.casing, strap: spec.strap, size: spec.size, style: spec.style,
      display: spec.display, colorHex: col ? col.hex : null,
      sensors: spec.sensors || [], brand,
    };
  },
  prodKey(spec) {
    return [spec.casing, spec.strap, spec.size, spec.style, spec.display, spec.color,
      (spec.sensors || []).join('+')].join('|');
  },
  // §8: specs are immutable after launch, so per-gen snapshots cache perfectly.
  // A proto Gen 1 (new model, still editable this quarter) keys by spec so edits show up.
  genKey(m, gv) {
    return gv.proto ? m.id + '-g' + gv.gen + '|' + this.prodKey(gv.spec) : m.id + '-g' + gv.gen;
  },
};
